/**
 * Wiring between the app's runtime and the organization scope.
 *
 * `orgScope` deliberately knows nothing about the auth store or React Query;
 * this module is the one place that connects them, so the scope module stays
 * unit-testable without dragging in cookies, Apollo or a QueryClient.
 *
 * Call `installOrgScope()` once, during bootstrap, before the first render.
 */

import type { QueryClient } from '@tanstack/react-query';

import { useAuthStoreV1 } from '@/stores/authStore_v1';

import { setActiveOrgId, signOutOrgScopedState, subscribeToOrgChange } from './orgScope';
import type { OrgId } from './types';

/**
 * Mirror `authStore_v1.selectedOrg` into the org scope.
 *
 * The auth store remains the source of truth for *which* org is selected; the
 * scope owns *what happens* when that changes. Zustand delivers this
 * subscription synchronously inside the `set()` that changed `selectedOrg`, so
 * every org-scoped store has re-pointed before React is told anything changed.
 */
export function connectOrgScopeToAuthStore(): () => void {
  let current: OrgId | null = useAuthStoreV1.getState().selectedOrg?.id ?? null;
  let signedIn = hasSession(useAuthStoreV1.getState());
  setActiveOrgId(current);

  return useAuthStoreV1.subscribe((state) => {
    const next = state.selectedOrg?.id ?? null;
    if (next !== current) {
      current = next;
      setActiveOrgId(next);
    }

    // Sign-out is not just "no org selected" — parked state for every
    // organization has to go too, or the next person to use this tab inherits
    // it. Ordered after the org change so stores re-point first and then drop
    // everything, rather than dropping and being handed one back.
    const nowSignedIn = hasSession(state);
    if (signedIn && !nowSignedIn) signOutOrgScopedState();
    signedIn = nowSignedIn;
  });
}

function hasSession(state: { token: string | null; user: unknown }): boolean {
  return Boolean(state.token || state.user);
}

/**
 * Evict the previous organization's cached queries on a switch.
 *
 * Almost every query key in this app already carries an org id, so a stale entry
 * cannot be *read* by the new org — different key, different cache entry. What
 * it can do is sit in memory for `gcTime` (30 minutes) holding a full metadata
 * tree per org visited. Removing the previous org's entries bounds that, and
 * guarantees that a key which is *missing* an org id (the class of bug this work
 * hunts) cannot serve Org A's response to Org B either.
 *
 * Note this removes rather than invalidates: invalidation would leave the data
 * in place and immediately refetch every observed query, which is the
 * "refetch the entire application on every switch" behaviour we want to avoid.
 * Removal drops the data; only the queries actually mounted afterwards refetch.
 */
export function connectOrgScopeToQueryCache(queryClient: QueryClient): () => void {
  return subscribeToOrgChange((_next, previous) => {
    if (!previous) return;
    queryClient.removeQueries({
      predicate: (query) => queryKeyMentionsOrg(query.queryKey, previous),
    });
  });
}

/**
 * True when any part of the key names `orgId` — including nested params objects,
 * which several hooks use (`agentKeys.list({ user_id, limit })`-style).
 */
function queryKeyMentionsOrg(queryKey: readonly unknown[], orgId: OrgId): boolean {
  return queryKey.some((part) => mentionsOrg(part, orgId));
}

function mentionsOrg(part: unknown, orgId: OrgId): boolean {
  if (part === orgId) return true;
  if (!part || typeof part !== 'object') return false;
  if (Array.isArray(part)) return part.some((item) => mentionsOrg(item, orgId));
  return Object.values(part as Record<string, unknown>).some((value) =>
    mentionsOrg(value, orgId),
  );
}

/**
 * Install every org-scope connection. Idempotent — a second call is a no-op, so
 * a hot-reloaded module does not end up with two subscriptions racing.
 */
let installed: (() => void) | null = null;

export function installOrgScope(queryClient: QueryClient): () => void {
  if (installed) return installed;

  const disconnectAuth = connectOrgScopeToAuthStore();
  const disconnectCache = connectOrgScopeToQueryCache(queryClient);

  installed = () => {
    disconnectAuth();
    disconnectCache();
    installed = null;
  };
  return installed;
}
